import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { environment } from '../env';
// Define the Profile interface
interface Profile {
  map: any;
  id?: number;
  name: string;
  jobTitle: string;
  techStack: string[];
  linkedin: string;
  github: string;
  email: string;
  profilePic?: string;
  tags?: string[];
}

// Create the API slice
export const profileApi = createApi({
  reducerPath: 'profileApi',
  baseQuery: fetchBaseQuery({ baseUrl: environment.apiUrl }),
  tagTypes: ['Profiles'],
  endpoints: (builder) => ({
    verifyToken: builder.mutation<{ isValid: boolean }, { token: string }>({
      query: ({ token }) => ({
        url: '/auth/verify-token',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`, // Send token in headers
        },
      }),
    }),
    getProfile: builder.query<Profile, void>({
      query: () => '/profiles',
      providesTags: ['Profiles']
    }),
    updateProfile: builder.mutation<Profile, Partial<Profile>>({
      query: (profile) => ({
        url: `/profiles/${profile.id}`,
        method: 'PATCH',
        body: profile,
      }),
      invalidatesTags: ['Profiles']
    }),
    createProfile: builder.mutation<Profile, Omit<Profile, 'id'>>({
      query: (profile) => ({
        url: '/profiles',
        method: 'POST',
        body: profile,
      }),
      invalidatesTags: ['Profiles']
    }),
    uploadImage: builder.mutation({
      query: (data) => ({
        url: '/files/upload',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['Profiles']
    }),
    optimizeImage: builder.mutation<{ filename: string; tags: string[] }, FormData>({
      query: (formData) => ({
        url: '/files/upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Profiles']
    }),
    deleteProfile: builder.mutation<void, number>({
      query: (id) => ({
        url: `/profiles/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

// Export hooks for usage in components
export const {
  useVerifyTokenMutation,
  useGetProfileQuery, 
  useUpdateProfileMutation, 
  useCreateProfileMutation,
  useUploadImageMutation,
  useDeleteProfileMutation,
  useOptimizeImageMutation
} = profileApi;
